/*
 * The field mission this device is out on (docs/GAME_DESIGN.md Part II, M1).
 *
 * "Start in Field Mode" on a mission card writes it here; the camera reads it
 * and stamps every new batch with the mission's id, so the outbox can bind the
 * collection (POST /collections mission_id). The server binds only while the
 * person holds the card, so a stale entry costs nothing but the chip.
 *
 * localStorage, not the batch store: it is one per device and per person, and
 * it must be there before the first photo of the next batch. It is keyed by
 * the signed-in person's id, so another account on the same phone sees no chip
 * for a mission it did not take. A mission's end (ends_at) clears it on read.
 */
import { useEffect, useState } from 'react';

export const ACTIVE_MISSION_KEY = 'mb_active_mission';
/** Fired on this tab when the active mission changes (the storage event only
 *  reaches OTHER tabs). */
export const MISSION_CHANGE_EVENT = 'pitriot:active-mission';

function announce() {
  try { window.dispatchEvent(new Event(MISSION_CHANGE_EVENT)); } catch { /* no window */ }
}

function storageOf(storage) {
  if (storage) return storage;
  try { return globalThis.localStorage; } catch { return null; }
}

export function readActiveMission(userId, storage, now = Date.now()) {
  const store = storageOf(storage);
  if (!store) return null;
  try {
    const raw = store.getItem(ACTIVE_MISSION_KEY);
    if (!raw) return null;
    const mission = JSON.parse(raw);
    if (!mission?.missionId || (userId && mission.userId && mission.userId !== userId)) return null;
    if (mission.until && Number.isFinite(Date.parse(mission.until)) && Date.parse(mission.until) < now) {
      store.removeItem(ACTIVE_MISSION_KEY);
      return null;
    }
    return mission;
  } catch {
    return null;
  }
}

/** `event`: a live field event (Part III), whose camera shows the team's
 *  mini-map and whose chip opens the event (/field/event/:id). */
export function setActiveMission({ missionId, title, until = null, userId = null, event = false }, storage) {
  const store = storageOf(storage);
  if (!store || !missionId) return null;
  const mission = { missionId, title: title || '', until, userId, since: new Date().toISOString(),
    ...(event ? { event: true } : {}) };
  try {
    store.setItem(ACTIVE_MISSION_KEY, JSON.stringify(mission));
  } catch {
    return null;
  }
  announce();
  return mission;
}

export function clearActiveMission(storage, missionId = null) {
  const store = storageOf(storage);
  if (!store) return;
  try {
    if (missionId) {
      const current = JSON.parse(store.getItem(ACTIVE_MISSION_KEY) || 'null');
      if (current && current.missionId !== missionId) return;
    }
    store.removeItem(ACTIVE_MISSION_KEY);
    announce();
  } catch {
    /* nothing to clear */
  }
}

/** The active mission for this person, following changes made here and in other tabs. */
export function useActiveMission(userId) {
  const [mission, setMission] = useState(() => readActiveMission(userId));
  useEffect(() => {
    setMission(readActiveMission(userId));
    const onStorage = (event) => {
      if (!event || event.key === null || event.key === ACTIVE_MISSION_KEY) setMission(readActiveMission(userId));
    };
    const onChange = () => setMission(readActiveMission(userId));
    window.addEventListener('storage', onStorage);
    window.addEventListener(MISSION_CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(MISSION_CHANGE_EVENT, onChange);
    };
  }, [userId]);
  return mission;
}
